/**
 * =============================================================================
 * Dashboard Page
 * =============================================================================
 *
 * Main dashboard showing overview statistics.
 *
 * Displays:
 * - Total institutions count (system_admin)
 * - Total devices count (admins)
 * - Total hands count (admins)
 * - Pending apps for approval (system_admin)
 * - My apps stats (app_dev)
 *
 * Note: User management is handled through Keycloak directly.
 */

import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { institutionsApi, devicesApi, handsApi, appsApi, healthApi } from '../services/api'
import { useAuth } from '../auth/AuthProvider'
import { resolveApiVersion } from '../utils/apiHealth'

// =============================================================================
// Stat Card Component
// =============================================================================

interface StatCardProps {
  title: string
  value: number | string
  icon: string
  loading?: boolean
  color?: string
  onClick?: () => void
}

/**
 * Dashboard statistic card.
 */
const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  loading = false,
  color = 'bg-primary-600',
  onClick,
}) => {
  return (
    <div
      className={`card flex items-center gap-4 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick}
    >
      <div className={`w-12 h-12 rounded-lg ${color} flex items-center justify-center text-2xl`}>
        {icon}
      </div>
      <div>
        <p className="text-sm text-gray-500">{title}</p>
        {loading ? (
          <div className="h-8 w-16 bg-gray-200 animate-pulse rounded" />
        ) : (
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        )}
      </div>
    </div>
  )
}

// =============================================================================
// Dashboard Page
// =============================================================================

const Dashboard: React.FC = () => {
  const navigate = useNavigate()
  const { user, hasRole } = useAuth()

  // Determine if user is an app developer (only game_dev role, not admin)
  const isGameDevOnly = hasRole('game_dev') && !hasRole('system_admin') && !hasRole('institution_admin')

  // Check if user is platform admin (system_admin or rehapiano_admin)
  const isPlatformAdmin = hasRole('system_admin') || hasRole('rehapiano_admin')

  // Fetch statistics
  const { data: institutions, isLoading: loadingInstitutions } = useQuery({
    queryKey: ['institutions'],
    queryFn: () => institutionsApi.list(),
    enabled: isPlatformAdmin,
  })

  const { data: devices, isLoading: loadingDevices } = useQuery({
    queryKey: ['devices'],
    queryFn: () => devicesApi.list(),
    enabled: !isGameDevOnly,
  })

  const { data: hands, isLoading: loadingHands } = useQuery({
    queryKey: ['hands'],
    queryFn: () => handsApi.list(),
    enabled: !isGameDevOnly,
  })

  // Fetch apps - for platform admins and app_dev
  const { data: apps, isLoading: loadingGames } = useQuery({
    queryKey: ['apps'],
    queryFn: () => appsApi.list(),
    enabled: isPlatformAdmin || hasRole('app_admin') || hasRole('game_dev') || hasRole('app_dev'),
  })

  // Fetch pending apps for platform admins
  const { data: pendingApps, isLoading: loadingPendingGames } = useQuery({
    queryKey: ['apps-pending'],
    queryFn: () => appsApi.listPending(),
    enabled: isPlatformAdmin || hasRole('app_admin'),
  })

  // Deployed BE version from GET /health (issue #19); '—' until loaded/on error
  const { data: health } = useQuery({
    queryKey: ['health'],
    queryFn: () => healthApi.get(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })
  const apiVersion = resolveApiVersion(health?.data?.version)

  return (
    <div className="space-y-6">
      {/* Welcome Message */}
      <div className="card">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Welcome, {user?.firstName}!
        </h2>
        <p className="text-gray-600">
          Here's an overview of the RehaPiano platform.
        </p>
      </div>

      {/* Statistics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Institutions - For platform admins */}
        {isPlatformAdmin && (
          <StatCard
            title="Institutions"
            value={institutions?.data?.length || 0}
            icon="🏥"
            loading={loadingInstitutions}
            color="bg-blue-600"
            onClick={() => navigate('/institutions')}
          />
        )}

        {/* Devices - Not for app_dev only */}
        {!isGameDevOnly && (
          <StatCard
            title="Devices"
            value={devices?.data?.length || 0}
            icon="🎹"
            loading={loadingDevices}
            color="bg-purple-600"
            onClick={() => navigate('/devices')}
          />
        )}

        {/* Hands - Not for app_dev only */}
        {!isGameDevOnly && (
          <StatCard
            title="Hands"
            value={hands?.data?.length || 0}
            icon="✋"
            loading={loadingHands}
            color="bg-orange-600"
            onClick={() => navigate('/hands')}
          />
        )}

        {/* Pending Apps - For platform admins and app_admin */}
        {(isPlatformAdmin || hasRole('app_admin')) && (
          <StatCard
            title="Pending Approval"
            value={pendingApps?.data?.length || 0}
            icon="⏳"
            loading={loadingPendingGames}
            color="bg-yellow-600"
            onClick={() => navigate('/apps?status=pending_approval')}
          />
        )}

        {/* My Apps - For app_dev */}
        {(hasRole('game_dev') || hasRole('app_dev')) && (
          <StatCard
            title="My Apps"
            value={apps?.data?.length || 0}
            icon="📱"
            loading={loadingGames}
            color="bg-green-600"
            onClick={() => navigate('/apps')}
          />
        )}

        {/* All Apps - For platform admins and app_admin */}
        {(isPlatformAdmin || hasRole('app_admin')) && (
          <StatCard
            title="Total Apps"
            value={apps?.data?.length || 0}
            icon="📱"
            loading={loadingGames}
            color="bg-indigo-600"
            onClick={() => navigate('/apps')}
          />
        )}
      </div>

      {/* Quick Stats Details - Not for app_dev only */}
      {!isGameDevOnly && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Devices by Status */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Devices by Status</h3>
            {loadingDevices ? (
              <div className="animate-pulse space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4" />
                <div className="h-4 bg-gray-200 rounded w-1/2" />
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Active</span>
                  <span className="badge badge-active">
                    {devices?.data?.filter(d => d.status === 'active').length || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Retired</span>
                  <span className="badge badge-inactive">
                    {devices?.data?.filter(d => d.status === 'retired').length || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Maintenance</span>
                  <span className="badge badge-warning">
                    {devices?.data?.filter(d => d.status === 'maintenance').length || 0}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Hands by Status */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Hands by Status</h3>
            {loadingHands ? (
              <div className="animate-pulse space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4" />
                <div className="h-4 bg-gray-200 rounded w-1/2" />
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Active</span>
                  <span className="badge badge-active">
                    {hands?.data?.filter(h => h.status === 'active').length || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Maintenance</span>
                  <span className="badge badge-warning">
                    {hands?.data?.filter(h => h.status === 'maintenance').length || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Left / Right</span>
                  <span className="text-sm text-gray-700">
                    {hands?.data?.filter(h => h.hand_type === 'left').length || 0} / {hands?.data?.filter(h => h.hand_type === 'right').length || 0}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* App Stats - For app developers and admins */}
      {(hasRole('game_dev') || hasRole('app_dev') || isPlatformAdmin || hasRole('app_admin')) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Apps by Status */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {isGameDevOnly ? 'My Apps by Status' : 'Apps by Status'}
            </h3>
            {loadingGames ? (
              <div className="animate-pulse space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4" />
                <div className="h-4 bg-gray-200 rounded w-1/2" />
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Draft</span>
                  <span className="badge bg-gray-100 text-gray-800">
                    {apps?.data?.filter(g => g.status === 'draft').length || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Pending Approval</span>
                  <span className="badge bg-yellow-100 text-yellow-800">
                    {apps?.data?.filter(g => g.status === 'pending_approval').length || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Approved</span>
                  <span className="badge badge-active">
                    {apps?.data?.filter(g => g.status === 'approved').length || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Rejected</span>
                  <span className="badge bg-red-100 text-red-800">
                    {apps?.data?.filter(g => g.status === 'rejected').length || 0}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Pending apps list - For platform admins and app_admin */}
          {(isPlatformAdmin || hasRole('app_admin')) && (
            <div className="card">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Pending Approval</h3>
                {(pendingApps?.data?.length || 0) > 0 && (
                  <button
                    onClick={() => navigate('/apps?status=pending_approval')}
                    className="text-sm text-primary-600 hover:text-primary-800"
                  >
                    View All
                  </button>
                )}
              </div>
              {loadingPendingGames ? (
                <div className="animate-pulse space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-4 bg-gray-200 rounded w-1/2" />
                </div>
              ) : pendingApps?.data && pendingApps.data.length > 0 ? (
                <div className="space-y-3">
                  {pendingApps.data.slice(0, 5).map((game) => (
                    <div
                      key={game.id}
                      className="flex justify-between items-center p-2 hover:bg-gray-50 rounded cursor-pointer"
                      onClick={() => navigate(`/apps/${game.id}`)}
                    >
                      <div>
                        <div className="font-medium text-gray-900">{game.name}</div>
                        <div className="text-xs text-gray-500">{game.developer_name}</div>
                      </div>
                      <span className="text-xs text-gray-400">
                        {new Date(game.submitted_at || game.created_at).toLocaleDateString('sk-SK')}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No apps pending approval.</p>
              )}
            </div>
          )}

          {/* Quick actions for app_dev */}
          {isGameDevOnly && (
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/apps/new')}
                  className="w-full btn-primary"
                >
                  Create New App
                </button>
                <button
                  onClick={() => navigate('/apps')}
                  className="w-full btn-secondary"
                >
                  View My Apps
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* System Info */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">System Information</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-500">API Version</span>
            <p className="font-medium">{apiVersion}</p>
          </div>
          <div>
            <span className="text-gray-500">Environment</span>
            <p className="font-medium">Development</p>
          </div>
          <div>
            <span className="text-gray-500">Your Roles</span>
            <div className="flex flex-wrap gap-1 mt-1">
              {user?.roles
                .filter(role => ['system_admin', 'rehapiano_admin', 'institution_admin', 'app_admin', 'app_dev', 'game_dev'].includes(role))
                .map(role => (
                  <span key={role} className="badge bg-gray-100 text-gray-800 text-xs capitalize">
                    {role.replace(/_/g, ' ')}
                  </span>
                ))}
            </div>
          </div>
          <div>
            <span className="text-gray-500">Session</span>
            <p className="font-medium text-green-600">Active</p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
